'use client';

import styled from '@emotion/styled';
import { Text } from '@template-project/afrodita-components';
import { ColorSelector } from '@/components/atoms/ColorSelector';
import type { PackProductSnapshot, PackVariantSnapshot } from '@/domain/entities/pack.entity';
import {
  buildOptionsFromVariants,
  isSwatchOption,
  resolveVariantFromOptions,
} from '@/lib/product/resolve-variant-from-options';

const SlotContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};

  &:last-child {
    border-bottom: none;
  }
`;

const SlotHeader = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  align-items: flex-start;
`;

const Thumbnail = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
`;

const ThumbnailPlaceholder = styled.div`
  width: 72px;
  height: 72px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductTitle = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

const OptionsButtonsContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const OptionButton = styled.button<{ $isSelected: boolean; $isAvailable: boolean }>`
  min-width: 40px;
  padding: 10px 14px;
  border: ${({ $isSelected, theme }) =>
    `1px solid ${$isSelected ? theme.colors.text.primary : theme.colors.border.color}`};
  background-color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.text.primary : 'transparent'};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.background.primary : theme.colors.text.primary};
  cursor: ${({ $isAvailable }) => ($isAvailable ? 'pointer' : 'not-allowed')};
  opacity: ${({ $isAvailable }) => ($isAvailable ? 1 : 0.5)};
  border-radius: 0;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
`;

export interface PackItemVariantSelectorProps {
  product: PackProductSnapshot;
  selectedVariantId?: string;
  onVariantChange: (variantId: string) => void;
  slotLabel?: string;
}

export function PackItemVariantSelector({
  product,
  selectedVariantId,
  onVariantChange,
  slotLabel,
}: PackItemVariantSelectorProps) {
  const variants = product.variants ?? [];
  const selectedVariant = variants.find((v) => v.id === selectedVariantId && v.enabled);
  const options = buildOptionsFromVariants(variants, product.options);

  const imageSrc =
    selectedVariant?.image_id &&
    product.images.find((img) => img.id === selectedVariant.image_id)?.src;

  const displayImage = imageSrc ?? product.images[0]?.src;

  const handleOptionChange = (optionName: string, optionValue: string) => {
    const matching = resolveVariantFromOptions(
      variants,
      selectedVariantId,
      optionName,
      optionValue,
    );
    if (matching) {
      onVariantChange(matching.id);
    }
  };

  if (variants.length <= 1) {
    return (
      <SlotContainer>
        <SlotHeader>
          {displayImage ? (
            <Thumbnail src={displayImage} alt={product.title} />
          ) : (
            <ThumbnailPlaceholder />
          )}
          <TitleBlock>
            <ProductTitle variant="body" weight="bold">
              {slotLabel ?? product.title}
            </ProductTitle>
          </TitleBlock>
        </SlotHeader>
      </SlotContainer>
    );
  }

  return (
    <SlotContainer>
      <SlotHeader>
        {displayImage ? (
          <Thumbnail src={displayImage} alt={product.title} />
        ) : (
          <ThumbnailPlaceholder />
        )}
        <TitleBlock>
          <ProductTitle variant="body" weight="bold">
            {slotLabel ?? product.title}
          </ProductTitle>
        </TitleBlock>
      </SlotHeader>

      {options.map((option) => {
        const availableValues = variants
          .filter((v) => v.enabled)
          .map((v) => v.variation_attributes?.find((a) => a.name === option.name)?.value)
          .filter(Boolean) as string[];

        return (
          <OptionSection key={option.name}>
            <OptionHeader>
              <Text size="small" variant="body" weight="bold">
                {option.name}:
              </Text>
              <Text size="small" variant="body" weight="regular">
                {selectedVariant?.variation_attributes?.find((a) => a.name === option.name)
                  ?.value ?? ''}
              </Text>
            </OptionHeader>

            {isSwatchOption(option, product.options) ? (
              <ColorSelector
                colors={option.values
                  .filter((v) => availableValues.includes(v))
                  .map((v) => ({ name: v }))}
                selectedColor={
                  selectedVariant?.variation_attributes?.find((a) => a.name === option.name)
                    ?.value
                }
                onColorChange={(colorName) => handleOptionChange(option.name, colorName)}
                size="md"
              />
            ) : (
              <OptionsButtonsContainer>
                {option.values.map((value) => {
                  const isAvailable = availableValues.includes(value);
                  const isSelected =
                    selectedVariant?.variation_attributes?.some(
                      (a) => a.name === option.name && a.value === value,
                    ) ?? false;

                  return (
                    <OptionButton
                      key={value}
                      type="button"
                      $isSelected={isSelected}
                      $isAvailable={isAvailable}
                      disabled={!isAvailable}
                      onClick={() => handleOptionChange(option.name, value)}
                    >
                      {value}
                    </OptionButton>
                  );
                })}
              </OptionsButtonsContainer>
            )}
          </OptionSection>
        );
      })}
    </SlotContainer>
  );
}
